/**
 * Slider module
 * Modern mobile touch slider with hardware accelerated transitions
 * @see http://idangero.us/swiper
 */
//import Swiper from 'swiper/bundle';
import { Swiper, Navigation, Pagination, Autoplay, Lazy, EffectFade } from 'swiper';
const Deepmerge = require('deepmerge');

Swiper.use([Navigation, Pagination, Autoplay, Lazy, EffectFade]);

export default class Slider {
	constructor(element = '.js-slider', options = {}) {
		this.element = element;
		this.defaults = {
			init: true,
			initialSlide: 0,
			direction: 'horizontal',
			speed: 1600,
			loop: true,
			effect: 'slide',
			autoHeight: false,
			parallax: false,
			preloadImages: true,
			observer: true,
			observeParents: true,
			lazy: {
				loadPrevNext: true,
				loadPrevNextAmount: 1,
				loadOnTransitionStart: true,
			},
			autoplay: {
				delay: 5000,
			},
			navigation: {
				prevEl: '',
				nextEl: '',
			},
			navigationType: 'bullets', // 'arrows', 'bullets', 'both', 'none'
			paginationType: 'progressbar', // 'bullets' | 'fraction' | 'progressbar' | 'custom' | false
			slidesPerView: 2,
			slidesPerGroup: 1,
			spaceBetween: 24,
			breakpoints: {
				480: {
					slidesPerView: 'auto',
					freeMode: true,
					spaceBetween: 48
				},
				768: {
					slidesPerView: 3,
					freeMode: false,
				},
				992: {
					slidesPerView: 4,
				},
				1200: {
					slidesPerView: 4,
				},
				1440: {
					slidesPerView: 4,
				},
			},
		};

		this.options = Deepmerge(this.defaults, options);
		this.sliders = {};

		this.init();
		window.addEventListener('NAVIGATE_END', () => this.init());
		window.addEventListener('infiniteScrollAppended', () => this.init());
	}

	init() {
		const sliderElements = document.querySelectorAll(this.element);

		sliderElements.forEach((sliderElement, index) => {
			let sliderId = sliderElement.id;

			if (!sliderId) {
				sliderId = `slider-${index}-${Date.now()}`;
				sliderElement.setAttribute('data-slider-id', sliderId);
				sliderElement.id = sliderId;
			}

			if (this.sliders[sliderId]) return;

			const navigationType = this.options.navigationType || 'arrows';
			const paginationType = this.options.paginationType || 'bullets';
			const sliderOptions = Deepmerge(this.options, {});

			// NAVIGATION
			if (navigationType === 'arrows' || navigationType === 'both') {
				let prevBtn = document.querySelector(`#slider-prev-${sliderId}`);
				let nextBtn = document.querySelector(`#slider-next-${sliderId}`);

				if (!prevBtn || !nextBtn) {
					prevBtn = document.createElement('div');
					nextBtn = document.createElement('div');

					prevBtn.id = `slider-prev-${sliderId}`;
					nextBtn.id = `slider-next-${sliderId}`;

					prevBtn.classList.add('swiper-button-prev');
					nextBtn.classList.add('swiper-button-next');

					// Insert buttons inside the slider element
					// sliderElement.appendChild(prevBtn);
					// sliderElement.appendChild(nextBtn);

					// Insert buttons after the slider element
					sliderElement.parentNode.insertBefore(prevBtn, sliderElement.nextSibling);
					sliderElement.parentNode.insertBefore(nextBtn, sliderElement.nextSibling);

				}

				sliderOptions.navigation = {
					prevEl: `#${prevBtn.id}`,
					nextEl: `#${nextBtn.id}`,
				};
			} else {
				sliderOptions.navigation = false;
			}

			// PAGINATION
			if (['bullets', 'fraction', 'progressbar', 'custom'].includes(paginationType)) {
				let paginationEl = sliderElement.querySelector('.swiper-pagination');

				if (!paginationEl) {
					paginationEl = document.createElement('div');
					paginationEl.classList.add('swiper-pagination');
					sliderElement.appendChild(paginationEl);
				}

				if (paginationType === 'custom') {
					sliderOptions.pagination = {
						el: paginationEl,
						clickable: true,
						type: 'custom',
						renderCustom: function (swiper, current, total) {
							const realSlides = swiper.slides.filter(slide => !slide.classList.contains('swiper-slide-duplicate'));
							let bullets = '';
							realSlides.forEach((_, index) => {
								const activeClass = index === swiper.realIndex ? ' swiper-pagination-bullet-active' : '';
								bullets += `<span class="swiper-pagination-bullet${activeClass}" data-index="${index}"></span>`;
							});
							return bullets;
						}
					};
				} else {
					sliderOptions.pagination = {
						el: paginationEl,
						clickable: true,
						type: paginationType,
					};
				}
			} else {
				sliderOptions.pagination = false;
			}

			// CREA SWIPER
			const swiperInstance = new Swiper(`#${sliderId}`, sliderOptions);
			this.sliders[sliderId] = swiperInstance;

			// GESTIONE CLICK BULLET PERSONALIZZATI
			if (paginationType === 'custom') {
				const paginationEl = sliderElement.querySelector('.swiper-pagination');
				if (paginationEl) {
					paginationEl.addEventListener('click', (e) => {
						if (e.target.matches('.swiper-pagination-bullet')) {
							const index = parseInt(e.target.getAttribute('data-index'));
							if (!isNaN(index)) {
								swiperInstance.slideTo(index);
							}
						}
					});
				}
			}
		});
	}

	autoplayStart(sliderId) {
		const slider = this.sliders[sliderId];
		if (slider) slider.autoplay.start();
	}

	autoplayStop(sliderId) {
		const slider = this.sliders[sliderId];
		if (slider) slider.autoplay.stop();
	}

	destroy(sliderId, deleteInstance = true, cleanStyles = true) {
		const slider = this.sliders[sliderId];
		if (slider) {
			slider.destroy(deleteInstance, cleanStyles);
			delete this.sliders[sliderId];
		}
	}

	slidePrev(sliderId, speed, runCallbacks) {
		const slider = this.sliders[sliderId];
		if (slider) slider.slidePrev(speed, runCallbacks);
	}

	slideNext(sliderId, speed, runCallbacks) {
		const slider = this.sliders[sliderId];
		if (slider) slider.slideNext(speed, runCallbacks);
	}

	slideTo(sliderId, index, speed, runCallbacks) {
		const slider = this.sliders[sliderId];
		if (slider) slider.slideTo(index, speed, runCallbacks);
	}
}
